import PropTypes from "prop-types";
import { Icon } from "@iconify/react";
import menu2Fill from "@iconify/icons-eva/menu-2-fill";
import RefreshFill from "@iconify/icons-eva/refresh-fill";
import arrowIosBackFill from "@iconify/icons-eva/arrow-ios-back-fill";
import arrowIosForwardFill from "@iconify/icons-eva/arrow-ios-forward-fill";
// material
import { alpha, styled } from "@mui/material/styles";
import {
  Box,
  Stack,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Tooltip,
} from "@mui/material";
// components
import { MHidden } from "../../components/@material-extend";
//
import AccountPopover from "./AccountPopover";
import { useDashboardTitle } from "./useDashboardTitle";
import ThemeModeToggle from "../../components/ThemeModeToggle";

// ----------------------------------------------------------------------

const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 92;

const RootStyle = styled(AppBar)(({ theme, isCollapsed }) => ({
  boxShadow: "none",
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)", // Fix on Mobile
  backgroundColor: alpha(
    theme.palette.background.paper,
    theme.palette.mode === "light" ? 0.92 : 0.6
  ),
  borderBottom:
    theme.palette.mode === "light"
      ? "1px solid transparent"
      : `1px solid ${alpha(theme.palette.divider, 0.6)}`,
  transition: theme.transitions.create(['width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  [theme.breakpoints.up("lg")]: {
    width: isCollapsed ? `calc(100% - 110px)` : `calc(100% - 250px)`,
    marginLeft: isCollapsed ? 110 : 250,
  },
}));

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: APPBAR_MOBILE,
  [theme.breakpoints.up("lg")]: {
    minHeight: APPBAR_DESKTOP,
    padding: theme.spacing(0, 5),
  },
}));

const interactiveIconStyles = (theme) => ({
  color: theme.palette.text.primary,
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  border:
    theme.palette.mode === "light"
      ? "1px solid transparent"
      : `1px solid ${alpha(theme.palette.divider, 0.8)}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1),
  boxShadow:
    theme.palette.mode === "light"
      ? theme.customShadows?.z4 || "0px 4px 12px rgba(15, 23, 42, 0.08)"
      : theme.customShadows?.z8 || "none",
  transition: theme.transitions.create(
    ['transform', 'background-color', 'box-shadow'],
    { duration: theme.transitions.duration.shorter }
  ),
  '&:hover': {
    backgroundColor: alpha(theme.palette.background.paper, 1),
    transform: 'translateY(-2px)',
    boxShadow: theme.customShadows?.z12 || theme.customShadows?.z8 || 'none',
  },
});

// ----------------------------------------------------------------------

DashboardNavbar.propTypes = {
  onOpenSidebar: PropTypes.func,
  isCollapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
};

export default function DashboardNavbar({ onOpenSidebar, isCollapsed, onToggleCollapse }) {
  const title = useDashboardTitle();
  return (
    <RootStyle isCollapsed={isCollapsed}>
      <ToolbarStyle>
        <MHidden width="lgUp">
          <IconButton
            onClick={onOpenSidebar}
            sx={{ mr: 1, color: "text.primary" }}
          >
            <Icon icon={menu2Fill} />
          </IconButton>
        </MHidden>

        {/* Desktop Sidebar Toggle Button */}
        <MHidden width="lgDown">
          <Tooltip title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}>
            <IconButton
              onClick={onToggleCollapse}
              sx={(theme) => ({ mr: 2, ...interactiveIconStyles(theme) })}
            >
              <Icon icon={isCollapsed ? arrowIosForwardFill : arrowIosBackFill} />
            </IconButton>
          </Tooltip>
        </MHidden>

        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: "text.secondary",
              fontSize: "24px",
              marginRight: 1.5,
            }}
          >
            {title}
          </Typography>
          <Tooltip placement="bottom" title="Reload">
            <IconButton
              onClick={() => window.location.reload()}
              sx={(theme) => interactiveIconStyles(theme)}
            >
              <Icon icon={RefreshFill} />
            </IconButton>
          </Tooltip>
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          spacing={{ xs: 0.5, sm: 1.5 }}
        >
          {/* <LanguagePopover /> */}
          {/* <NotificationsPopover /> */}
          <ThemeModeToggle />
          <AccountPopover />
        </Stack>
      </ToolbarStyle>
    </RootStyle>
  );
}